import styled from "@emotion/styled";
import {
  Button,
  Dialog,
  DialogActions,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  Field,
  MessageBar,
  Select,
  Spinner,
} from "@fluentui/react-components";
import { ArrowImportRegular, Dismiss24Filled } from "@fluentui/react-icons";
import { AsyncSelect, ThemeProvider } from "@greco/components";
import { searchInsurers } from "api/api";
import { Buffer } from "buffer";
import { toast } from "components/FluentToast";
import { VSpace } from "components/Spacer";
import { Flex } from "components/styled";
import debounce from "debounce-promise";
import moment from "moment";
import { DefaultButton, Icon } from "office-ui-fabric-react";
import React, { useCallback, useState } from "react";
import { useDropzone } from "react-dropzone";
import { useTranslation } from "react-i18next";
import { useAppStoreState } from "store/AppStore";
import { useInsurerReportsStore } from "store/InsurerReportsStore";
import { getShortInsurerName } from "utils/utils";
import { resolveInsurerReportDate } from "../insurerReportUtils";

const FormSection = styled.section`
  display: flex;
  flex-wrap: wrap;
  background-color: rgba(201, 201, 201, 0.1);
`;

const FormSectionItem100 = styled.div`
  width: 100%;
  margin: 9px;
`;

const FileName = styled.div`
  font-size: 20px;
  text-align: center;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const Title = styled.div`
  font-size: 20px;
`;

const UploadIcon = styled(Icon)`
  font-size: 35px;
`;

export const ImportInsurerReportDialog = () => {
  const [
    { importInsurerReportDialog, importInsurerReportStatus },
    {
      setInsurerReportsStoreValue,
      setInsurerReportsStoreValues,
      importInsurerReport,
      loadInsurerReports,
    },
  ] = useInsurerReportsStore();
  const { taxonomy, grecoCompanies, updatedGrecoCompanies, appState } =
    useAppStoreState();
  const { t } = useTranslation();

  const isLoading = importInsurerReportStatus === "loading";

  const [name, setName] = React.useState("");
  const [file, setFile] = useState(null);
  const [selectedInsurer, setSelectedInsurer] = useState<any>(null);

  const defaultCompanyNumber =
    grecoCompanies && grecoCompanies.length === 1
      ? String(grecoCompanies[0].companyNumber)
      : "";
  const [grecoCompanyNumber, setGrecoCompanyNumber] =
    useState<string>(defaultCompanyNumber);

  const eurId = taxonomy?.Currency?.byCode?.["Currency.EUR"]?.id;
  const [currencyCode, setCurrencyCode] = useState<string>(
    eurId !== undefined && eurId !== null ? String(eurId) : ""
  );

  const previousMonth = moment().subtract(1, "month");
  const [year, setYear] = useState<string>(String(previousMonth.year()));
  const [month, setMonth] = useState<string>(String(previousMonth.month())); // 0-based

  const currentYear = moment().year();
  const yearOptions: number[] = [];
  for (let y = currentYear; y >= currentYear - 6; y--) {
    yearOptions.push(y);
  }

  const currencyItems: any[] = taxonomy?.Currency?.items ?? [];

  const onDrop = useCallback((acceptedFiles) => {
    acceptedFiles.forEach((f) => {
      const reader = new FileReader();
      reader.onload = () => {
        const binaryStr: any = reader.result;
        const encode = Buffer.from(binaryStr).toString("base64");
        setFile({ base64str: encode, name: f.name });
        setName(f.name);
      };
      reader.readAsArrayBuffer(f);
    });
  }, []);

  const { getRootProps, getInputProps, open } = useDropzone({
      noDragEventsBubbling: true,
      noClick: true,
      noKeyboard: true,
      accept: {
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": [
          ".xlsx",
        ],
        "application/vnd.ms-excel": [".xls"],
      },
      multiple: false,
      onDrop,
    });

  const insurerId = selectedInsurer?.value?.id ?? null;
  const isValid =
    !!file && insurerId !== null && grecoCompanyNumber !== "" && currencyCode !== "";

  const closeDialog = () => {
    setInsurerReportsStoreValue("importInsurerReportDialog", {
      isOpen: false,
    });
  };

  return (
    <Dialog open={importInsurerReportDialog.isOpen}>
      <DialogSurface>
        <DialogBody>
          <DialogTitle>{t("pct.importInsurerReport.label")}</DialogTitle>
          <DialogContent>
            <VSpace height={10} />
            <MessageBar
              data-help="insurer-reports-import-warning"
              intent="warning"
            >
              {t("pct.importInsurerReportWarning.label")}
            </MessageBar>
            <VSpace height={15} />

            <Field
              data-help="insurer-reports-import-insurer"
              label={t("pct.insurer.label")}
            >
              <ThemeProvider isDarkMode={appState.darkMode}>
                <AsyncSelect
                id="importInsurerSearch"
                loadOptions={debounce(async (input) => {
                  const trimmedInput = input.trim();
                  if (trimmedInput.length === 0) return [];
                  const dataList = await searchInsurers(trimmedInput);
                  let retVal = [];
                  dataList.data.forEach((insurer) => {
                    let insName = insurer.name;
                    const shortName = getShortInsurerName(insurer.id + "");
                    if (shortName !== "") {
                      insName = shortName;
                    }
                    retVal.push({
                      value: insurer,
                      label:
                        insName +
                        (insurer.internalNumber
                          ? ` (${insurer.internalNumber})`
                          : ""),
                    });
                  });
                  return retVal;
                }, 300)}
                clearable
                isClearable
                menuPlacement="auto"
                menuPosition="fixed"
                menuPortalTarget={document.body}
                styles={{
                  // escape the DialogSurface overflow clipping; above Fluent v9 dialog layer
                  menuPortal: (base) => ({ ...base, zIndex: 2000000 }),
                }}
                placeholder={t("pct.searchInsurers.label")}
                noOptionsMessage={() => t("greco.noResults")}
                loadingMessage={() => t("greco.searching")}
                isDisabled={isLoading}
                value={selectedInsurer}
                onChange={(value) => setSelectedInsurer(value)}
                />
              </ThemeProvider>
            </Field>
            <VSpace height={10} />

            <Flex gap={10}>
              <Field label={t("pct.grecoCompanyNumber.label")}>
                <Select
                  data-help="insurer-reports-import-company"
                  disabled={isLoading}
                  value={grecoCompanyNumber}
                  onChange={(e, data) => setGrecoCompanyNumber(data.value)}
                >
                  <option value="">{""}</option>
                  {(grecoCompanies ?? []).map((gc) => (
                    <option
                      key={gc.companyNumber}
                      value={String(gc.companyNumber)}
                    >
                      {gc.companyShort ?? gc.companyNumber}
                    </option>
                  ))}
                </Select>
              </Field>
              <Field label={t("pct.currency.label")}>
                <Select
                  data-help="insurer-reports-import-currency"
                  disabled={isLoading}
                  value={currencyCode}
                  onChange={(e, data) => setCurrencyCode(data.value)}
                >
                  <option value="">{""}</option>
                  {currencyItems.map((el) => (
                    <option key={el.id} value={String(el.id)}>
                      {t(el.code)}
                    </option>
                  ))}
                </Select>
              </Field>
            </Flex>
            <VSpace height={10} />

            <Flex gap={10}>
              <Field label={t("pct.year.label")}>
                <Select
                  data-help="insurer-reports-import-year"
                  disabled={isLoading}
                  value={year}
                  onChange={(e, data) => setYear(data.value)}
                >
                  {yearOptions.map((y) => (
                    <option key={y} value={String(y)}>
                      {y}
                    </option>
                  ))}
                </Select>
              </Field>
              <Field label={t("pct.month.label")}>
                <Select
                  data-help="insurer-reports-import-month"
                  disabled={isLoading}
                  value={month}
                  onChange={(e, data) => setMonth(data.value)}
                >
                  {Array.from({ length: 12 }, (_, i) => i).map((m) => (
                    <option key={m} value={String(m)}>
                      {moment().month(m).format("MMMM")}
                    </option>
                  ))}
                </Select>
              </Field>
            </Flex>
            <VSpace height={15} />

            <FormSection>
              <FormSectionItem100>
                <div {...getRootProps()} data-help="insurer-reports-import-file">
                  <input {...(getInputProps() as any)} />
                  {file ? (
                    <Flex direction="column" gap={10}>
                      <Flex gap={5} justifyContent="center">
                        <Icon
                          style={{ fontSize: "25px" }}
                          iconName="ExcelDocument"
                        />
                        <FileName title={file.name}>{file.name}</FileName>
                        <Button
                          appearance="subtle"
                          icon={<Dismiss24Filled />}
                          onClick={() => setFile(null)}
                          disabled={isLoading}
                        />
                      </Flex>
                      <DefaultButton
                        text={t("pct.chooseDifferentFile")}
                        onClick={open}
                        disabled={isLoading}
                      />
                    </Flex>
                  ) : (
                    <Flex direction="column" gap={5}>
                      <UploadIcon iconName="CloudUpload" />
                      <div style={{ textAlign: "center" }}>
                        <Title>{t("pct.dragExcelFileHere")}</Title>
                        <Title>{t("pct.or")}</Title>
                      </div>
                      <DefaultButton text={t("pct.browseFiles")} onClick={open} />
                    </Flex>
                  )}
                </div>
              </FormSectionItem100>
            </FormSection>
            <VSpace height={20} />
          </DialogContent>
          <DialogActions>
            <Button
              appearance="secondary"
              disabled={isLoading}
              onClick={closeDialog}
            >
              {t("pct.close.label")}
            </Button>
            <Button
              data-help="insurer-reports-import-submit"
              appearance="primary"
              disabled={isLoading || !isValid}
              icon={
                isLoading ? (
                  <Spinner size="tiny" />
                ) : (
                  <ArrowImportRegular fontSize={16} />
                )
              }
              onClick={() => {
                const insurerReportDate = resolveInsurerReportDate(
                  parseInt(year, 10),
                  parseInt(month, 10)
                );
                importInsurerReport(
                  {
                    name: name,
                    base64String: file.base64str,
                    insurerId: insurerId,
                    grecoCompanyNumber: parseInt(grecoCompanyNumber, 10),
                    currencyCode: parseInt(currencyCode, 10),
                    insurerReportDate: insurerReportDate,
                  },
                  () => {
                    setInsurerReportsStoreValues({
                      importInsurerReportDialog: {
                        isOpen: false,
                      },
                    });
                    toast.success(t("greco.success"));
                    loadInsurerReports({
                      taxonomy,
                      grecoCompanies,
                      t,
                      updatedGrecoCompanies,
                    });
                  }
                );
              }}
            >
              {t("pct.import.label")}
            </Button>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
};
